/**
 * Aspect — Plan model mapping (pure logic, no DOM / no engine)
 * --------------------------------------------------------------
 * px↔world conversion, footprint sizing, and furniture constraint solving for
 * the 2D plan editor. Extracted from plan_editor.js so the coordinate math is
 * testable in Node and reusable outside the SVG layer. All functions take the
 * plan model object explicitly; none touch the editor instance.
 *
 * The model carries: metersPerPixel, floor, edges, pxBounds { minPx, minPy,
 * maxPx, maxPy }, graphMeterBounds { minMx, minMz, maxMx, maxMz }, originPx.
 */

import { resolveFurnitureXZ } from "../scene/furniture_placement.js";

const DEFAULT_MPP = 0.02;

/** Editor is in floor-only mode (no traced walls, floor rect/polygon only). */
export function isFloorOnlyModel(model) {
  return Boolean(model && model.edges.length === 0 && model.floor);
}

/** Absolute meter spans of the traced graph envelope, when known. */
export function meterSpans(model) {
  const gb = model?.graphMeterBounds;
  if (!gb) return null;
  const spanMx = gb.maxMx - gb.minMx;
  const spanMz = gb.maxMz - gb.minMz;
  if (!(spanMx > 0) || !(spanMz > 0)) return null;
  return { spanMx, spanMz };
}

/** Plan pixel -> world meters (origin at the plan's min corner). */
export function pxToWorld(model, px, py) {
  const mpp = model?.metersPerPixel > 0 ? model.metersPerPixel : DEFAULT_MPP;
  const spans = meterSpans(model);
  if (isFloorOnlyModel(model) || (model?.floor && !spans)) {
    return { x: px * mpp, z: py * mpp };
  }
  const pb = model?.pxBounds;
  if (pb && spans) {
    const spanPxX = pb.maxPx - pb.minPx || 1;
    const spanPxZ = pb.maxPy - pb.minPy || 1;
    return {
      x: ((px - pb.minPx) / spanPxX) * spans.spanMx,
      z: ((py - pb.minPy) / spanPxZ) * spans.spanMz,
    };
  }
  const o = model?.originPx ?? { px: 0, py: 0 };
  return { x: (px - o.px) * mpp, z: (py - o.py) * mpp };
}

/** World meters -> plan pixel (inverse of pxToWorld). */
export function worldToPx(model, x, z) {
  const mpp = model?.metersPerPixel > 0 ? model.metersPerPixel : DEFAULT_MPP;
  const spans = meterSpans(model);
  if (isFloorOnlyModel(model) || (model?.floor && !spans)) {
    return { px: x / mpp, py: z / mpp };
  }
  const pb = model?.pxBounds;
  if (pb && spans) {
    const spanPxX = pb.maxPx - pb.minPx || 1;
    const spanPxZ = pb.maxPy - pb.minPy || 1;
    return {
      px: pb.minPx + (x / spans.spanMx) * spanPxX,
      py: pb.minPy + (z / spans.spanMz) * spanPxZ,
    };
  }
  const o = model?.originPx ?? { px: 0, py: 0 };
  return { px: o.px + x / mpp, py: o.py + z / mpp };
}

/** Absolute graph meters for a traced pixel (inverse of buildFromGraph shift). */
export function pxToGraphMeters(model, px, py) {
  const mpp = model?.metersPerPixel > 0 ? model.metersPerPixel : DEFAULT_MPP;
  const gb = model?.graphMeterBounds;
  const pb = model?.pxBounds;
  const spans = meterSpans(model);
  if (gb && pb && spans) {
    const { x, z } = pxToWorld(model, px, py);
    return { x: gb.minMx + x, y: gb.minMz + z };
  }
  return { x: px * mpp, y: py * mpp };
}

/** Footprint width/depth in plan pixels (matches 3D catalog meters). */
export function footprintPx(model, fp) {
  const w = fp?.w ?? 1;
  const d = fp?.d ?? 1;
  const pb = model?.pxBounds;
  const spans = meterSpans(model);
  if (pb && spans) {
    const spanPxX = pb.maxPx - pb.minPx || 1;
    const spanPxZ = pb.maxPy - pb.minPy || 1;
    return { wPx: (w / spans.spanMx) * spanPxX, dPx: (d / spans.spanMz) * spanPxZ };
  }
  const mpp = model?.metersPerPixel > 0 ? model.metersPerPixel : DEFAULT_MPP;
  return { wPx: w / mpp, dPx: d / mpp };
}

/** Meter envelope of graph nodes (absolute, pre min-corner shift), or null. */
export function graphMeterBoundsFromGraph(graph) {
  let minMx = Infinity;
  let minMz = Infinity;
  let maxMx = -Infinity;
  let maxMz = -Infinity;
  for (const n of graph?.nodes ?? []) {
    const mx = Number(n.x);
    const mz = Number(n.y);
    if (!Number.isFinite(mx) || !Number.isFinite(mz)) continue;
    minMx = Math.min(minMx, mx);
    minMz = Math.min(minMz, mz);
    maxMx = Math.max(maxMx, mx);
    maxMz = Math.max(maxMz, mz);
  }
  if (!Number.isFinite(minMx)) return null;
  return { minMx, minMz, maxMx, maxMz };
}

/**
 * Keep a furniture center in valid floor space (same rules as 3D placement).
 * @returns {{ px: number, py: number }} constrained plan-pixel position
 */
export function constrainFurniturePlanPx(model, room, placements, px, py, footprint, rotY = 0, scale = 1, excludePlacementId = null) {
  if (!room) return { px, py };
  const { x, z } = pxToWorld(model, px, py);
  const resolved = resolveFurnitureXZ(
    x,
    z,
    { footprint, rotation_y: rotY, scale, excludeId: excludePlacementId },
    room,
    placements ?? [],
  );
  return worldToPx(model, resolved.x, resolved.z);
}
